import { useMemo, useState } from "react"
import styled from "styled-components"

import {
  Select,
  spacing,
  Text,
  TextArea,
  TextField,
  TSelectOptions,
} from "@minuthq/meatball-ui-react"
import { setHours, startOfHour } from "date-fns"
import { FormikProps } from "formik"

import { HomesDropdown } from "src/components/Dropdown/HomesDropdown"
import { TMaybeHome } from "src/data/homes/types/homeTypes"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useTaskTypeLabels } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { isUnassignedOptionId } from "src/data/taskManagement/taskManagementFilters"
import {
  TTaskPostBody,
  TTaskTemplate,
  TTaskType,
} from "src/data/taskManagement/taskManagementTypes"
import { useFetchTaskTemplates } from "src/data/taskManagement/taskTemplateQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { ComboboxStateful } from "src/ui/Combobox/ComboboxStateful"
import { MDateTimePicker } from "src/ui/MDateTimePicker/MDateTimePicker"
import { Show } from "src/ui/Show/Show"

import { AssigneeCombobox } from "./AssigneeCombobox"

export function TaskForm({
  form,
  homes,
  isCreateMode = false,
}: {
  form: FormikProps<TTaskPostBody>
  homes?: TMaybeHome[]
  isCreateMode?: boolean
}) {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()
  const { orgId } = useOrganization()

  const [selectedTemplateId, setSelectedTemplateId] =
    useState<string>("no-template")
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>("")

  const taskTypes = useTaskTypeLabels()

  const fetchTaskTemplates = useFetchTaskTemplates({
    orgId,
    filters: { limit: 50, offset: 0 }, // Filters not yet implemented in the backend
  })

  const taskTemplates = useMemo(() => {
    return fetchTaskTemplates.data?.templates || []
  }, [fetchTaskTemplates.data?.templates])

  const filteredTemplates = useMemo(() => {
    if (!templateSearchQuery) return taskTemplates
    return taskTemplates.filter((template: TTaskTemplate) =>
      template.title.toLowerCase().includes(templateSearchQuery.toLowerCase())
    )
  }, [taskTemplates, templateSearchQuery])

  const homeOption = homes?.find((h) => h?.home_id === form.values.home_id)

  const taskCategory: TSelectOptions<TTaskType> = Object.entries(taskTypes).map(
    ([type, label]) => ({
      label,
      value: type as TTaskType,
    })
  )

  const templateOptions = useMemo(() => {
    const baseOptions = [
      {
        label: "No template",
        value: "no-template",
      },
    ]

    const templateOpts = filteredTemplates.map((template: TTaskTemplate) => ({
      label: template.title,
      value: template.template_id,
    }))

    return [...baseOptions, ...templateOpts]
  }, [filteredTemplates])

  function handleTemplateSelection(templateId: string) {
    setSelectedTemplateId(templateId)

    if (templateId === "no-template") {
      form.setValues({
        title: "",
        home_id: form.values.home_id,
        category: "" as TTaskType,
        description: "",
        status: form.values.status,
        assigned_to: { type: "unassigned" },
        time_window: {
          due_date: "",
          earliest_start_date: undefined,
        },
      })
    } else {
      const selectedTemplate = taskTemplates.find(
        (template: TTaskTemplate) => template.template_id === templateId
      )
      if (selectedTemplate) {
        form.setValues({
          ...form.values,
          title: selectedTemplate.task_title,
          category: selectedTemplate.task_category,
          description: selectedTemplate.task_description || "",
        })
      }
    }
  }

  function handleDateTimeChange(value: Date | null) {
    return form.setValues((prev) => {
      if (!value) {
        return {
          ...prev,
        }
      }

      return {
        ...prev,
        time_window: {
          ...prev.time_window,
          due_date: value.toISOString(),
        },
      }
    })
  }

  const selectedTemplateLabel = useMemo(() => {
    const template = templateOptions.find(
      (option) => option.value === selectedTemplateId
    )
    return template?.label || ""
  }, [templateOptions, selectedTemplateId])

  return (
    <FormBox>
      <div>
        <Show if={isCreateMode}>
          <Text marginBottom={spacing.L}>
            {t(langKeys.task_management_create_task_dialog_description)}
          </Text>
          <ComboboxStateful
            label="Template"
            options={templateOptions}
            selectedValue={selectedTemplateId}
            initialInput={selectedTemplateLabel}
            onChange={(value) => {
              handleTemplateSelection(value)
            }}
            onSearch={setTemplateSearchQuery}
          />
        </Show>
      </div>

      <div>
        <Select
          label={`${t(langKeys.task_management_task_type)} *`}
          value={form.values.category}
          options={[
            {
              label: t(langKeys.task_management_select_a_task),
              value: "",
              disabled: true,
            },
            ...taskCategory,
          ]}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              category: value as TTaskType,
            }))
          }}
          required
        />
      </div>

      <div>
        <HomesDropdown
          value={homeOption ?? null}
          onSelect={(home) => {
            form.setValues((prev) => ({
              ...prev,
              home_id: home?.home_id,
            }))
          }}
          required={true}
        />
      </div>

      <div>
        <TextField
          label={`${t(langKeys.task_management_task_title)} *`}
          value={form.values.title}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              title: value,
            }))
          }}
          required
        />
      </div>

      <div>
        <TextArea
          label={t(langKeys.task_management_task_description)}
          value={form.values.description ?? ""}
          onChange={(value) => {
            form.setValues((prev) => ({
              ...prev,
              description: value,
            }))
          }}
          minRows={3}
          maxRows={5}
        />
      </div>

      <div>
        <MDateTimePicker
          datePickerLabel={t(langKeys.task_management_task_due_date)}
          timePickerInputProps={{
            clockType: user.clock_type,
          }}
          initialDate={
            form.values.time_window.due_date
              ? new Date(form.values.time_window.due_date)
              : startOfHour(setHours(new Date(), 12))
          }
          key={form.values.time_window.due_date}
          onChange={handleDateTimeChange}
          required
        />
      </div>

      <div>
        <AssigneeCombobox
          label={t(langKeys.task_management_task_assign_to)}
          homeId={form.values.home_id}
          initialValue={form.values.assigned_to?.assignee_id ?? ""}
          onChange={(value) => {
            if (isUnassignedOptionId(value)) {
              form.setValues((prev) => ({
                ...prev,
                assigned_to: { type: "unassigned" },
              }))
            } else {
              form.setValues((prev) => ({
                ...prev,
                assigned_to: {
                  type: "member",
                  assignee_id: value,
                },
              }))
            }
          }}
        />
      </div>
    </FormBox>
  )
}

const FormBox = styled.div`
  display: grid;
  gap: ${spacing.L};
`
